import Tooltip from '@/components/dataDisplay/Tooltip';
import Question, { QuestionProps } from '@/modules/chatbot/components/Question';
import SingleSelectInput from '@/modules/chatbot/components/SingleSelectInput';
import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_EVENTS,
} from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import findNextQuestionToAsk from '@/modules/core/chatbot/findNextQuestionToAsk';
import { ChatbotQuestions } from '@/modules/core/chatbot/getQuestionsToAsk';
import { shouldAnimateCurrentQuestion } from '@/modules/core/chatbot/shouldAnimateCurrentQuestion';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import UpdatedQuestion from '@/modules/core/data/types/CDE/UpdatedQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { ModChatbot } from '@/modules/core/data/types/ModChatbot';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { ReactSelectGroup } from '@/modules/core/reactSelect/types/ReactSelect';
import { useTheme } from '@emotion/react';
import classnames from 'classnames';
import isEqual from 'lodash.isequal';
import size from 'lodash.size';
import React, {
  Dispatch,
  SetStateAction,
} from 'react';

type Props = {
  questions: ChatbotQuestions;
  activeQuestion: ChatbotQuestion;
  context: ChatbotContext;
  setActiveQuestion: Dispatch<SetStateAction<ChatbotQuestion>>;
  setContext: Dispatch<SetStateAction<ChatbotContext>>;
  updatedQuestion: UpdatedQuestion;
  setUpdatedQuestion: Dispatch<SetStateAction<UpdatedQuestion>>;
  updatedQuestionAnimationDuration: number;
  logEvent: LogEvent;
  bubbleTimerDuration: number;
  allEducationalPrograms: AirtableRecord<EducationalProgram>[];
  allEducationalProgramSteps: AirtableRecord<EducationalProgramStep>[];
  allCampuses: AirtableRecord<Campus>[];
  allStudentSolutionsCategories: AirtableRecord<StudentSolutionsCategory>[];
  modChatbot: AirtableRecord<ModChatbot>;
}

/**
 * Question about the category the user's interested in.
 *
 * Displayed last, unless deactivated.
 */
const CategoryQuestion: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    questions,
    activeQuestion,
    context,
    setActiveQuestion,
    setContext,
    updatedQuestion,
    setUpdatedQuestion,
    updatedQuestionAnimationDuration,
    logEvent,
    bubbleTimerDuration,
    allEducationalPrograms,
    allEducationalProgramSteps,
    allCampuses,
    allStudentSolutionsCategories,
    modChatbot,
  } = props;
  const {
    categoryQuestion,
  } = questions;
  const theme: CustomerTheme = useTheme();

  return (
    <Question
      question={categoryQuestion}
      activeQuestion={activeQuestion}
      context={context}
      bubbleTimerDuration={bubbleTimerDuration}
    >
      {
        (props: QuestionProps): JSX.Element => {
          const {
            question,
          } = props;

          return (
            <div className={classnames({ [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, question) })}>
              <div
                className={classnames('message')}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: question.label,
                  }}
                />
              </div>
              <div
                className={classnames('message user select-input')}
              >
                <SingleSelectInput
                  question={question}
                  selectedValue={context.categories?.[0]}
                  getOptionLabel={(step: StudentSolutionsCategory): string => {
                    return step?.label;
                  }}
                  onChange={(selectedChoice: StudentSolutionsCategory): void => {
                    const newSelectedChoices: StudentSolutionsCategory[] = [selectedChoice];

                    if (!isEqual(newSelectedChoices, context[question.ref])) {
                      const nextContext: ChatbotContext = {
                        ...context,
                        [question.ref]: newSelectedChoices,
                      };

                      setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, modChatbot, setContext));

                      setUpdatedQuestion(question);
                      setTimeout(() => {
                        setUpdatedQuestion(null);
                      }, updatedQuestionAnimationDuration);

                      logEvent(AMPLITUDE_EVENTS.CHATBOT_ANSWER, {
                        question: {
                          ref: question.ref,
                          inputType: 'select',
                          isMultiChoices: false,
                          rank: question.order,
                        },
                        selection: {
                          ref: selectedChoice.ref || '_ALL', // If no ref, it means no category was selected and thus displaying them all
                        },
                        action: AMPLITUDE_ACTIONS.SELECT,
                      });
                    }
                  }}
                  formatGroupLabel={(data: ReactSelectGroup): JSX.Element => {
                    const {
                      label: categoryLabel,
                      labelShort: categoryLabelShort,
                      options: steps,
                    } = data;

                    return (
                      <div>
                        {
                          // Display label short if available (with tooltip to help remember what category it means)
                          categoryLabelShort ? (
                            <Tooltip overlay={<span>{categoryLabel}</span>}>
                              <span className={'select-group-label'}>
                                {categoryLabelShort}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className={'select-group-label'}>
                              {categoryLabel}
                            </span>
                          )
                        }

                        <span className={'select-group-items-counter'}>
                          {size(steps)}
                        </span>
                      </div>
                    );
                  }}
                  theme={theme}
                />
              </div>
            </div>
          );
        }
      }
    </Question>
  );
};

export default CategoryQuestion;
